import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Receipt, RefreshCw, Send, CheckCircle, Search, ChevronDown } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FinancialTabNav from '../components/FinancialTabNav';
import api from '../services/api';

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};

const AdminFinancialReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/financial/receipts?limit=100');
      setReceipts(res.data.receipts || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  const handleResend = async (receiptId) => {
    setResending(receiptId);
    try {
      const res = await api.post(`/admin/financial/receipts/${receiptId}/resend`);
      toast.success(res.data.message);
      fetchReceipts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend receipt');
    } finally {
      setResending(null);
    }
  };

  return (
    <Sidebar>
      <FinancialTabNav />
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-4">

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{total} receipts</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : receipts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Receipt className="w-8 h-8 mb-2" />
                <p className="text-sm">No receipts yet</p>
                <p className="text-xs mt-1">Receipts are generated when payments are processed</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Company</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Plan</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">Amount</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-500">Sent</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {receipts.map(receipt => (
                      <tr key={receipt.id} className="hover:bg-gray-50/50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{receipt.company_name || '—'}</p>
                          {receipt.company_email && <p className="text-xs text-gray-400">{receipt.company_email}</p>}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{receipt.plan_name || '—'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          ${typeof receipt.amount === 'number' ? receipt.amount.toFixed(2) : receipt.amount}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(receipt.created_at)}</td>
                        <td className="px-4 py-3 text-center">
                          {receipt.receipt_sent ? (
                            <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleResend(receipt.id)}
                            disabled={resending === receipt.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                          >
                            <Send className="w-3 h-3" />
                            {resending === receipt.id ? 'Sending...' : receipt.receipt_sent ? 'Resend' : 'Send'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </Sidebar>
  );
};

export default AdminFinancialReceipts;
